
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';

import * as Objects from '@core/objects';
import { VersionShareItem } from '@core/models/version-share.model';
import { VersionSharesService } from '@core/services/version-shares/version-shares.service';

import { UrlService } from '@core/services/url/url.service';
import { ThreeDotsMenuItem } from '@core/components/helpers/navigation/three-dots-menu/three-dots-menu-item';
import { FilesListItemMenuClick } from '@core/components/files/lists/files-list-item-menu-click';
import { VersionListItemMenuClick } from '@core/components/versions/list/version-list-item-menu-click';

import { LogbookService } from 'app/services/app/logbook.service';
import { ProjectsService } from 'app/services/app/projects.service';
import { AppRouterService } from 'app/services/app/router.service';
import { VersionsService } from 'app/services/app/versions.service';
import { AppService } from 'app/services/app/app.service';


@Component({
    selector: 'project-details-logbook-properties-request-properties',
    templateUrl: './request-properties.component.html',
    styleUrls: ['./request-properties.component.scss']
})
export class ProjectDetailsLogbookEventRequestPropertiesComponent implements OnInit, OnDestroy{
    protected readonly ACCESS_REQUEST_FEEDBACK = 'share_feedback';


    public readonly PROJECT_ADDED = 'requests.added';
    public readonly LABEL_ADDED = 'requests.labels.added';
    public readonly LABEL_REMOVED = 'requests.labels.removed';
    public readonly FILE_ADDED = 'requests.files.added';
    public readonly FILE_REMOVED = 'requests.files.removed';
    public readonly STAGE_UPDATED = 'requests.status.updated';
    public readonly MESSAGE_ADDED = 'requests.messages.added';
    public readonly MESSAGE_REMOVED = 'requests.messages.removed';
    public readonly ASSIGNED = 'requests.assigned.updated';
    public readonly DUE_DATE = 'requests.due_date.updated';
    public readonly ARTWORK_ADDED = 'requests.versions.added';
    public readonly INFO_UPDATED = "requests.information.updated";
    public readonly FORM_SUBMISSION_ADDED = "custom_forms.requests.submission.added";

	@Input() event: Objects.LogbookEvent;

    @Input() properties: any = null;

    public messagePrefixUrl: string = '';
    public notes: number;
    public menu: Array<ThreeDotsMenuItem> = [];
    // version details
    public feedbackPluginAccess: boolean = false;
    public feedbacks: Array<VersionShareItem> = null;
    public menuVersion: Array<ThreeDotsMenuItem> = [{
        title: "See Artwork Details",
        key: "details",
        icon: "see-details"
    }];

    protected subscriptor: Subscription;
    protected notesSubscriptor: Subscription;
    protected feedbacksSubscriptor: Subscription;

    constructor(private versionSharesSrv: VersionSharesService, private appRouter: AppRouterService, 
        private logbookSrv: LogbookService,
        private urlSrv: UrlService,
        private app: AppService,
        private versionsSrv: VersionsService,
        private projectsSrv: ProjectsService){}
    
    ngOnInit(): void {
        if(this.event != null){
            this.init();
        }
    }

    ngOnDestroy(): void {
        if(this.notesSubscriptor)
            this.notesSubscriptor.unsubscribe();
        if(this.feedbacksSubscriptor)
            this.feedbacksSubscriptor.unsubscribe();
        if(this.subscriptor)
            this.subscriptor.unsubscribe();
    }

    private init() : void {

        // set prefix url for messages...
        if(this.event.type == this.MESSAGE_ADDED || this.event.type == this.MESSAGE_REMOVED )    
            this.messagePrefixUrl = '/requests/'+ this.properties.request.id;
        // get notes in case of files
        if(this.event.type == this.FILE_ADDED || this.event.type == this.FILE_REMOVED) { 
            // we need order here for multiple items on menu
            this.menu = [
                {
                    title: "Download",
                    key: "download",
                    icon: "download",
                    order: 1
                }];
            // only if event action is not removed user can see notes or open file
             // file must comes with active property
            // if(this.event.type != this.FILE_REMOVED && this.properties.file.active){
            //     this.menu.push({
            //         title: "See Notes",
            //         key: "notes",
            //         icon: "notes",
            //         order: 2
            //     });
            // }
            
            this.notesSubscriptor = this.projectsSrv.selectProjectFilesNotesCounter().subscribe((counters: Objects.AnnotationsCounter[]) => {
                if (counters && Array.isArray(counters)) {
                    const fileCounter = counters.find((c: Objects.AnnotationsCounter) => c.file?.id == this.properties.file.id);
                    this.notes = fileCounter ? fileCounter.annotations?.count : 0;
                } else {
                    this.notes = 0;
                }
            })

        }

        // in case of version added
        if(this.event.type == this.ARTWORK_ADDED){
            // get plugins access
            this.subscriptor = this.app.getSession().subscribe( (session: any) => {
                this.feedbackPluginAccess = session.options.plugins.some((pa) => pa == this.ACCESS_REQUEST_FEEDBACK);
                this.initFeedbacks([this.properties.version]);
            });
        }
    }

    onClickPreview(file: Objects.File) : void {
        // prevent action for removed or future removed files 
        // file comes with active property: || !this.properties.file.active
        if(this.event.type == this.FILE_REMOVED || !this.properties.file.active)
            return;
        
        if (file != null && this.properties.request != null){
            this.appRouter.navigate('/projects/'+this.properties.request.id + "/files/"+file.id);
        }        
    }

    onClickVersionPreview(file: Objects.File){
        if (file != null && this.properties.version != null){
            this.appRouter.navigate('/projects/'+this.properties.request.id + '/versions/'+this.properties.version.id + "/files/"+file.id);
        }
    }

    /**
     * 
     * To migrate to new files model to public download
     */
    onActionDownloadClick(file: Objects.File){
        window.location.href = this.urlSrv.build('/files/'+file.id);
    }

    onMenuClick(menu: FilesListItemMenuClick) : void {
        switch(menu.key) {
            case "notes":
                this.onClickPreview(menu.file);
                break;
            case "download":
                window.location.href = this.urlSrv.build('/files/'+menu.file.id);
                break;
        }
    }

    onVersionMenuClick(menu: VersionListItemMenuClick) {
        // console.debug(" menu original click",menu);
        switch(menu.key) {
            case "details":
                // url to go to Artwork Details
                this.appRouter.navigate('/projects/'+this.properties.request.id + "/versions/"+menu.version.id);
                break;
        }
    }

    protected initFeedbacks(versions: Array<Objects.Version>) {
        if(!this.feedbackPluginAccess) {
            this.feedbacks = [];
            return;
        }

        // @deprecated oct-2026: legacy share feedback bubbles url call, replaced by VersionSharesService.getVersionShareItems$
        // this.feedbacksSubscriptor = this.versionsSrv.getFeedbacks$(versions).subscribe((feedbacks: Array<VersionShareItem>) => {
        this.feedbacksSubscriptor = this.versionSharesSrv.getVersionShareItems$(versions).subscribe((feedbacks: Array<VersionShareItem>) => {
            // console.log("GET FEEDBACKS ARE ", feedbacks)
            this.feedbacks = feedbacks.filter((f: VersionShareItem) => f.version.id == this.properties.version.id);
                // this.status = this.STATUS_DONE;
        });
    }
}